import React, { useEffect, useMemo } from 'react';

/**
 * Schema-driven inputs panel — Clarifications + Scope + Date Range +
 * Rep Selection + Remove-Test-Data, with each section opted-in/out per
 * the report's `parameters` schema. Used by both RunNowDialog and
 * EventEditor's per-item editor.
 *
 * Schema knobs that affect rendering (all from
 * `backend/Reports/ReportTypes.cs`):
 *   - clarifications: string[]      — read-only info bullets at the top
 *   - omitScope: bool               — hide the scope section entirely
 *   - omitDateRange: bool           — hide the date range section entirely
 *   - supportsRepSelection: bool    — render the rep picker
 *   - supportsRemoveTestData: bool  — render the test-data toggle
 *   - supportsRemoveSelfReferrals: bool — render the self-referral toggle
 *   - allowedScopeKinds, defaultScopeKind, allowedDateModes, defaultDateRange
 *
 * Owns no state; the parent passes `value` and `onChange` so the same form
 * fits naturally inside both a one-shot run and a list of items.
 *
 * Value shape:
 *   {
 *     scope: { type: 'customer'|'saved_filter'|'all'|'facility_ids',
 *              customerId?, savedFilterId?, facilityIds?: string[] },
 *     dateRange: { mode, n?, start?, end? },
 *     repSelection: { mode: 'all'|'specific', repUserIds?: string[] },
 *     removeTestData: boolean,
 *     removeSelfReferrals?: boolean,  // only when schema.supportsRemoveSelfReferrals
 *     consultEventNames?: string[],   // only when schema.supportsScheduleEventAliases
 *     procedureEventNames?: string[], // only when schema.supportsScheduleEventAliases
 *   }
 */

const DATE_MODE_LABELS = {
  last_n_days: 'Last N days',
  previous_calendar_week: 'Previous calendar week',
  previous_calendar_month: 'Previous calendar month',
  previous_quarter: 'Previous quarter',
  absolute: 'Custom (absolute)',
};

const SCOPE_LABELS = {
  customer: 'Customer',
  saved_filter: 'Saved Filter',
  all: 'All Facilities',
  facility_ids: 'Custom Facility Set',
};

function todayString() {
  const d = new Date();
  const z = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
}

// Parse the comma-separated Schedule Event alias inputs. Dedupes while
// preserving user order so the summary sheet lists aliases the way the
// user typed them.
function parseCommaList(csv) {
  if (!csv) return [];
  const seen = new Set();
  const out = [];
  for (const raw of String(csv).split(',')) {
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

function repDisplayName(r) {
  const full = `${r.firstName || ''} ${r.lastName || ''}`.trim();
  return full || r.email || r.id;
}

function ReportParamsForm({
  schema,
  value,
  onChange,
  customers = [],
  savedFilters = [],
  csReps = [],
  onPickFacilityIds,
  showRemoveTestData = true,
}) {
  const scope = value?.scope || { type: 'all' };
  const dateRange = value?.dateRange || { mode: 'last_n_days', n: 30 };
  const repSelection = value?.repSelection || { mode: 'all' };

  const omitScope = !!schema?.omitScope;
  const omitDateRange = !!schema?.omitDateRange;
  const supportsRepSelection = !!schema?.supportsRepSelection;
  const clarifications = schema?.clarifications || [];

  const allowedScopes = useMemo(
    () => (schema?.allowedScopeKinds || ['customer', 'saved_filter', 'all', 'facility_ids']),
    [schema]
  );
  const allowedDateModes = useMemo(
    () => (schema?.allowedDateModes || Object.keys(DATE_MODE_LABELS)),
    [schema]
  );

  // Make sure the scope kind is one the schema actually allows; if not,
  // fall back to the schema default. Skipped when scope is omitted entirely
  // because the value isn't surfaced in the UI in that case.
  useEffect(() => {
    if (omitScope) return;
    if (!allowedScopes.includes(scope.type)) {
      onChange({
        ...value,
        scope: { type: schema?.defaultScopeKind || allowedScopes[0] || 'all' },
      });
    }
  }, [omitScope, allowedScopes, scope.type, schema, value, onChange]);

  useEffect(() => {
    if (omitDateRange) return;
    if (!allowedDateModes.includes(dateRange.mode)) {
      onChange({
        ...value,
        dateRange: schema?.defaultDateRange || { mode: 'last_n_days', n: 30 },
      });
    }
  }, [omitDateRange, allowedDateModes, dateRange.mode, schema, value, onChange]);

  // Backfill a default repSelection so values produced by older clients
  // (no repSelection key at all) don't render as "all" while writing
  // nothing to the wire. Only runs when the schema asks for it.
  useEffect(() => {
    if (!supportsRepSelection) return;
    if (!value?.repSelection) {
      onChange({ ...value, repSelection: { mode: 'all' } });
    }
  }, [supportsRepSelection, value, onChange]);

  const setScope = (next) => onChange({ ...value, scope: next });
  const setDate = (next) => onChange({ ...value, dateRange: next });
  const setRepSelection = (next) => onChange({ ...value, repSelection: next });
  const setRemoveTestData = (next) => onChange({ ...value, removeTestData: next });
  const setRemoveSelfReferrals = (next) => onChange({ ...value, removeSelfReferrals: next });
  const setEventNames = (key, csv) => onChange({ ...value, [key]: parseCommaList(csv) });

  const selectedRepIds = new Set(repSelection.repUserIds || []);
  const toggleRep = (id) => {
    const next = new Set(selectedRepIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setRepSelection({ mode: 'specific', repUserIds: Array.from(next) });
  };

  return (
    <div className="rpf-root">
      {clarifications.length > 0 && (
        <ul className="rpf-clarifications">
          {clarifications.map((c, i) => (
            <li key={i}>{c}</li>
          ))}
        </ul>
      )}

      {!omitScope && (
        <div className="rpf-section">
          <label className="rpf-label">Scope</label>
          <div className="rpf-scope-tabs">
            {allowedScopes.map(kind => (
              <button
                key={kind}
                type="button"
                className={scope.type === kind ? 'is-active' : ''}
                onClick={() => setScope({ type: kind })}
              >{SCOPE_LABELS[kind] || kind}</button>
            ))}
          </div>

          {scope.type === 'customer' && (
            <select
              value={scope.customerId || ''}
              onChange={(e) => setScope({ type: 'customer', customerId: e.target.value ? Number(e.target.value) : null })}
            >
              <option value="">— Pick a customer —</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>
                  {c._depth ? '\u00A0\u00A0\u00A0\u00A0' : ''}{c.name}
                </option>
              ))}
            </select>
          )}

          {scope.type === 'saved_filter' && (
            <select
              value={scope.savedFilterId || ''}
              onChange={(e) => setScope({ type: 'saved_filter', savedFilterId: e.target.value ? Number(e.target.value) : null })}
            >
              <option value="">— Pick a filter —</option>
              {savedFilters.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          )}

          {scope.type === 'all' && (
            <div className="rpf-hint">Every facility the cached data has touched.</div>
          )}

          {scope.type === 'facility_ids' && (
            <div className="rpf-row">
              <span className="rpf-hint">
                {scope.facilityIds?.length
                  ? `${scope.facilityIds.length} facilities selected`
                  : 'No facilities picked yet'}
              </span>
              {onPickFacilityIds && (
                <button
                  type="button"
                  className="rpf-btn-secondary"
                  onClick={() => onPickFacilityIds(scope.facilityIds || [], (ids) => setScope({ type: 'facility_ids', facilityIds: ids }))}
                >Choose…</button>
              )}
            </div>
          )}
        </div>
      )}

      {!omitDateRange && (
        <div className="rpf-section">
          <label className="rpf-label">Date range</label>
          <div className="rpf-row">
            <select
              value={dateRange.mode}
              onChange={(e) => {
                const mode = e.target.value;
                if (mode === 'last_n_days') {
                  setDate({ mode, n: dateRange.n || 30 });
                } else if (mode === 'absolute') {
                  setDate({ mode, start: dateRange.start || todayString(), end: dateRange.end || todayString() });
                } else {
                  setDate({ mode });
                }
              }}
            >
              {allowedDateModes.map(m => (
                <option key={m} value={m}>{DATE_MODE_LABELS[m] || m}</option>
              ))}
            </select>

            {dateRange.mode === 'last_n_days' && (
              <>
                <input
                  type="number"
                  min="1"
                  max="3650"
                  value={dateRange.n ?? 30}
                  onChange={(e) => setDate({ ...dateRange, n: Number(e.target.value) || 30 })}
                  style={{ width: 80 }}
                />
                <span className="rpf-hint">days</span>
              </>
            )}

            {dateRange.mode === 'absolute' && (
              <>
                <input
                  type="date"
                  value={dateRange.start || ''}
                  onChange={(e) => setDate({ ...dateRange, start: e.target.value })}
                />
                <span className="rpf-dash">–</span>
                <input
                  type="date"
                  value={dateRange.end || ''}
                  onChange={(e) => setDate({ ...dateRange, end: e.target.value })}
                />
              </>
            )}
          </div>
        </div>
      )}

      {supportsRepSelection && (
        <div className="rpf-section">
          <label className="rpf-label">Run for</label>
          <div className="rpf-scope-tabs">
            <button
              type="button"
              className={repSelection.mode === 'all' ? 'is-active' : ''}
              onClick={() => setRepSelection({ mode: 'all' })}
            >All CS reps</button>
            <button
              type="button"
              className={repSelection.mode === 'specific' ? 'is-active' : ''}
              onClick={() => setRepSelection({
                mode: 'specific',
                repUserIds: repSelection.repUserIds || [],
              })}
            >Specific reps</button>
          </div>

          {repSelection.mode === 'all' && (
            <div className="rpf-hint">
              {csReps.length > 0
                ? `Will run for all ${csReps.length} eligible rep${csReps.length === 1 ? '' : 's'} at the time the report fires — newly added reps automatically join future runs.`
                : 'No eligible CS reps yet. Assign customers as Acts-as-CS-Rep in the Customer Manager (or User Manager) to populate this list.'}
            </div>
          )}

          {repSelection.mode === 'specific' && (
            <>
              {csReps.length === 0 ? (
                <div className="rpf-hint">
                  No eligible CS reps yet. Assign customers as Acts-as-CS-Rep in the Customer Manager (or User Manager) to populate this list.
                </div>
              ) : (
                <>
                  <div className="rpf-row" style={{ marginTop: 4 }}>
                    <button
                      type="button"
                      className="rpf-btn-secondary"
                      onClick={() => setRepSelection({
                        mode: 'specific',
                        repUserIds: csReps.map(r => r.id),
                      })}
                      disabled={selectedRepIds.size === csReps.length}
                    >Select all</button>
                    <button
                      type="button"
                      className="rpf-btn-secondary"
                      onClick={() => setRepSelection({ mode: 'specific', repUserIds: [] })}
                      disabled={selectedRepIds.size === 0}
                    >Clear</button>
                    <span className="rpf-hint">
                      {selectedRepIds.size} of {csReps.length} selected
                    </span>
                  </div>
                  <div className="rpf-rep-list">
                    {csReps.map(r => (
                      <label key={r.id} className="rpf-rep-row">
                        <input
                          type="checkbox"
                          checked={selectedRepIds.has(r.id)}
                          onChange={() => toggleRep(r.id)}
                        />
                        <span className="rpf-rep-name">
                          {repDisplayName(r)}
                          {r.isActive === false && <span className="rpf-rep-tag"> · inactive</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                  {selectedRepIds.size === 0 && (
                    <div className="rpf-warn">Pick at least one rep, or switch back to "All CS reps".</div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

      {schema?.supportsScheduleEventAliases && (
        <div className="rpf-section">
          <label className="rpf-label">Schedule Event names</label>
          <div className="rpf-hint" style={{ marginBottom: 6 }}>
            Enter the exact event names your facilities use in VitalEngine, separated by commas.
            The report matches case-insensitively and takes the earliest matching event per referral.
          </div>
          <div className="rpf-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
            <label className="rpf-label" style={{ fontWeight: 500 }}>Consult events</label>
            <input
              type="text"
              placeholder="e.g. Initial Consult, Clinic, Clinic Visit"
              defaultValue={(value?.consultEventNames || []).join(', ')}
              onBlur={(e) => setEventNames('consultEventNames', e.target.value)}
            />
            <label className="rpf-label" style={{ fontWeight: 500, marginTop: 8 }}>Procedure events</label>
            <input
              type="text"
              placeholder="e.g. Procedure, Case, OR Date"
              defaultValue={(value?.procedureEventNames || []).join(', ')}
              onBlur={(e) => setEventNames('procedureEventNames', e.target.value)}
            />
          </div>
        </div>
      )}

      {showRemoveTestData && schema?.supportsRemoveTestData !== false && (
        <div className="rpf-section">
          <label className="rpf-checkbox">
            <input
              type="checkbox"
              checked={value?.removeTestData ?? true}
              onChange={(e) => setRemoveTestData(e.target.checked)}
            />
            Remove test data (recommended)
          </label>
        </div>
      )}

      {schema?.supportsRemoveSelfReferrals && (
        <div className="rpf-section">
          <label className="rpf-checkbox">
            <input
              type="checkbox"
              checked={value?.removeSelfReferrals ?? false}
              onChange={(e) => setRemoveSelfReferrals(e.target.checked)}
            />
            Remove self-referrals
          </label>
          <div className="rpf-hint" style={{ marginTop: 4 }}>
            Drops referrals where the Created By facility matches the Receiving facility
            (same facility entered and received the referral).
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Returns null if `value` satisfies `schema`, else a human-readable error.
 * Centralized so RunNowDialog and EventEditor stay in sync on what counts
 * as a complete set of inputs.
 */
export function validateReportParams(schema, value) {
  if (!schema) return null;
  const v = value || {};

  if (!schema.omitScope) {
    const s = v.scope || {};
    if (s.type === 'customer' && !s.customerId) return 'Pick a customer.';
    if (s.type === 'saved_filter' && !s.savedFilterId) return 'Pick a saved filter.';
    if (s.type === 'facility_ids' && !(s.facilityIds && s.facilityIds.length)) return 'Pick at least one facility.';
  }

  if (!schema.omitDateRange) {
    const dr = v.dateRange || {};
    if (dr.mode === 'absolute' && (!dr.start || !dr.end)) return 'Pick start and end dates.';
  }

  if (schema.supportsRepSelection) {
    const rs = v.repSelection || { mode: 'all' };
    if (rs.mode === 'specific' && !(rs.repUserIds && rs.repUserIds.length)) {
      return 'Pick at least one rep, or switch to "All CS reps".';
    }
  }

  if (schema.supportsScheduleEventAliases) {
    const consult = v.consultEventNames || [];
    const procedure = v.procedureEventNames || [];
    if (consult.length === 0 && procedure.length === 0) {
      return 'Enter at least one consult or procedure event name.';
    }
  }

  return null;
}

export default ReportParamsForm;
